import React from 'react';
import { motion } from 'framer-motion';
import { PlayIcon } from 'lucide-react';
import { EASE_OUT } from '../../utils/motion';
export type StructureKind = 'strategy' | 'content' | 'system' | 'analysis';
const systemNodes = [[1, 1], [4, 1], [2, 3], [5, 3], [1, 5], [4, 5]];
const systemLinks = [[0, 2], [1, 2], [2, 3], [2, 4], [3, 5], [1, 3]];
const bars = [28, 38, 34, 52, 64, 88];
export function StructureVisual({
  kind
}: {
  kind: StructureKind;
}) {
  if (kind === 'strategy') {
    return <svg viewBox="0 0 200 200" className="h-44 w-44 md:h-52 md:w-52" aria-hidden="true" data-id="element-263" data-mp="1izs1p41x0b56">
        {[84, 58, 32].map(r => <circle key={r} cx="130" cy="70" r={r} fill="none" stroke="#1D1D1F" strokeOpacity="0.12" data-id="element-264" data-mp="147zlkpuaqt2e" />)}
        <motion.line x1="24" y1="178" x2="130" y2="70" stroke="#1D1D1F" strokeWidth="1.5" initial={{
        pathLength: 0
      }} whileInView={{
        pathLength: 1
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.3,
        ease: EASE_OUT
      }} data-id="element-265" data-mp="196uc6dyi9lx2" />
        <circle cx="24" cy="178" r="4" fill="#1D1D1F" data-id="element-266" data-mp="226ep2jx9hf9a" />
        <circle cx="130" cy="70" r="7" fill="#007C8C" data-id="element-267" data-mp="227zfpyggge1v" />
      </svg>;
  }
  if (kind === 'content') {
    return <div className="relative h-44 w-40 md:h-52 md:w-48" aria-hidden="true" data-id="element-268" data-mp="2c42srzcuhx3l">
        <div className="absolute inset-x-6 top-0 h-full rounded-2xl bg-graphite/[0.06]" data-id="element-269" data-mp="2c1zsmmhzanl5" />
        <div className="absolute inset-x-3 top-3 h-full rounded-2xl bg-graphite/[0.1]" data-id="element-270" data-mp="1oero46sdjik6" />
        <div className="absolute inset-x-0 top-6 flex h-full flex-col rounded-2xl bg-graphite p-3" data-id="element-271" data-mp="1jgyl1mmdb4o4">
          <div className="grid flex-1 place-items-center rounded-xl bg-white/[0.08]" data-id="element-272" data-mp="1y5uspikiifo7">
            <PlayIcon className="h-6 w-6 fill-white text-white" data-id="element-273" data-mp="1tct9iyd9vtyt" />
          </div>
          <div className="mt-3 h-1.5 w-2/3 rounded-full bg-white/30" data-id="element-274" data-mp="15n7cagggnotg" />
          <div className="mt-1.5 h-1.5 w-1/3 rounded-full bg-white/15" data-id="element-275" data-mp="10p5qvovkbdet" />
        </div>
      </div>;
  }
  if (kind === 'system') {
    const cell = 32;
    return <svg viewBox="0 0 208 208" className="h-44 w-44 md:h-52 md:w-52" aria-hidden="true" data-id="element-276" data-mp="1qct3x80g3jc8">
        {Array.from({
        length: 7
      }).map((_, row) => Array.from({
        length: 7
      }).map((__, col) => <circle key={`${row}-${col}`} cx={8 + col * cell} cy={8 + row * cell} r="1.5" fill="rgba(255,255,255,0.18)" data-id="element-277" data-mp="1z7g3hnjfh1gk" data-mpi={col} />))}
        {systemLinks.map(([a, b], i) => <motion.line key={i} x1={8 + systemNodes[a][0] * cell} y1={8 + systemNodes[a][1] * cell} x2={8 + systemNodes[b][0] * cell} y2={8 + systemNodes[b][1] * cell} stroke="#00E5FF" strokeOpacity="0.5" initial={{
        pathLength: 0
      }} whileInView={{
        pathLength: 1
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.3,
        delay: i * 0.05,
        ease: EASE_OUT
      }} data-id="element-278" data-mp="17g07x3zw84e7" data-mpi={i} />)}
        {systemNodes.map(([x, y], i) => <circle key={i} cx={8 + x * cell} cy={8 + y * cell} r={i === 2 ? 7 : 4.5} fill="#00E5FF" data-id="element-279" data-mp="l6bc5sn8irpe" data-mpi={i} />)}
      </svg>;
  }
  return <div className="flex h-44 items-end gap-2.5 md:h-52 md:gap-3" aria-hidden="true" data-id="element-280" data-mp="g772z20b8tz2">
      {bars.map((h, i) => <motion.div key={i} initial={{
      scaleY: 0
    }} whileInView={{
      scaleY: 1
    }} viewport={{
      once: true
    }} transition={{
      duration: 0.3,
      delay: i * 0.05,
      ease: EASE_OUT
    }} style={{
      height: `${h}%`,
      originY: 1
    }} className={`w-5 rounded-md md:w-6 ${i === bars.length - 1 ? 'bg-cyan-deep' : 'bg-graphite/15'}`} data-id="element-281" data-mp="bdvs8psdnt1v" data-mpi={i} />)}
    </div>;
}